import { yupResolver } from "@hookform/resolvers/yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Button,
  Container,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { object, string } from "yup";
import { useLoginMutation } from "../../Store/Services/AuthService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { clearBrowserSession } from "../../Utils/sessionCleanup";

const schema = object({
  username: string()
    .trim()
    .email("Please enter a valid Email!")
    .required("Please fill out your email"),
  password: string().required("Please fill out your password"),
}).required();

const defaultButtonSx = {
  textTransform: "none",
  borderRadius: "0.65rem",
  height: "2.7rem",
  fontWeight: 600,
  fontFamily: '"Sora", "Segoe UI", sans-serif',
  background: "linear-gradient(135deg, #0f6b83, #215c72)",
  boxShadow: "0 10px 18px rgba(15, 107, 131, 0.28)",
};

const centeredLabelFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: "4.8rem",
  },
  "& .MuiOutlinedInput-input": {
    padding: "1.2rem 0.9rem",
    fontSize: "1.1rem",
  },
  "& .MuiInputLabel-root": {
    top: "50%",
    transform: "translate(14px, -50%) scale(1)",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    top: 0,
    transform: "translate(14px, -9px) scale(0.75)",
  },
};

const SignInForm = ({
  onSuccess,
  showTopLabel = true,
  topLabel = "POS Sign In",
  submitLabel = "Sign In",
  submitButtonSx,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isError, setIsError] = useState();
  const [triggerLogin, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();

  const handleClickShowPassword = () => {
    setShowPassword((currentShowState) => !currentShowState);
  };

  const onSubmit = async (data) => {
    try {
      const { roles, access_token, refresh_token } = await triggerLogin(data).unwrap();

      // A session that ended by closing the browser (rather than by logging
      // out) leaves the previous user's floor state, delivery orders and
      // invoice history behind in localStorage. Start every login from a clean
      // slate so a shared terminal never carries data between users.
      clearBrowserSession();

      sessionStorage.setItem("role", roles);
      sessionStorage.setItem("access_token", access_token);
      sessionStorage.setItem("refresh_token", refresh_token);
      sessionStorage.setItem("isSignedIn", 1);
      // Post-login housekeeping (the cart lookup in SignInPage/SignInDialog)
      // must never gate the redirect. The session is already established above,
      // so a slow cart API should not strand the user on the sign-in screen.
      await Promise.race([
        Promise.resolve()
          .then(() => onSuccess?.())
          .catch((setupErr) =>
            console.error("Post-login setup failed: ", setupErr)
          ),
        new Promise((resolve) => setTimeout(resolve, 4000)),
      ]);

      navigate("/");
      window.location.reload();
    } catch (err) {
      console.error("Failed to save the user: ", err);
      setIsError(err?.data?.message);
      toast.error(err?.data?.message, { position: "bottom-right" });
    }
  };

  return (
    <Container disableGutters>
      {showTopLabel && (
        <Typography
          sx={{
            fontFamily: '"Sora", "Segoe UI", sans-serif',
            fontWeight: 600,
            fontSize: "0.82rem",
            letterSpacing: "0.09em",
            textTransform: "uppercase",
            color: "#497284",
          }}
        >
          {topLabel}
        </Typography>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl fullWidth margin="normal" error={!!errors?.username}>
          <InputLabel htmlFor="login-email">Email</InputLabel>
          <OutlinedInput
            id="login-email"
            {...register("username")}
            label="Email"
            fullWidth
            autoComplete="username"
            sx={centeredLabelFieldSx}
          />
          <FormHelperText>{errors.username?.message}</FormHelperText>
        </FormControl>

        <FormControl fullWidth margin="normal" error={!!errors.password}>
          <InputLabel htmlFor="login-password">Password</InputLabel>
          <OutlinedInput
            id="login-password"
            {...register("password")}
            label="Password"
            fullWidth
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            sx={centeredLabelFieldSx}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword} edge="end" aria-label="Show password">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          <FormHelperText>{errors.password?.message}</FormHelperText>
        </FormControl>

        <div style={{ marginTop: "1.2rem" }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading}
            aria-label="Sign in"
            sx={{ ...defaultButtonSx, ...(submitButtonSx || {}) }}
          >
            {submitLabel}
          </Button>

          {isError && (
            <Typography sx={{ marginTop: "0.8rem", color: "#b44343", fontSize: "0.9rem" }}>
              {isError}
            </Typography>
          )}
        </div>
      </form>
    </Container>
  );
};

export default SignInForm;
